<i18n lang="yaml">
en:
  title: 'Scalable Network Disks'
  subtitle: 'Scalable network volumes are persistent block devices that can be&nbsp;used on&nbsp;any cloud server. Three network volumes are available:'
  characteristics: 'Characteristics'
  blockTitle1: 'Basic'
  blockTitle2: 'Universal'
  blockTitle3: 'Fast'
  blockSubtitle1: 'For storing large amounts of&nbsp;data that do&nbsp;not need to&nbsp;be&nbsp;frequently read or&nbsp;overwritten.'
  blockSubtitle2: 'For hosting system disks of&nbsp;virtual machines.'
  blockSubtitle3: 'For workloads that require increased I/O performance.'
  buttonText: 'Price calculator'
  month: 'month'
  gb: 'GB'
ru:
  title: 'Сетевые масштабируемые диски'
  subtitle: 'Сетевые диски&nbsp;&mdash; масштабируемые блочные устройства, которые можно легко переносить между серверами в
              облаке. Трехкратная репликация обеспечивает высокую сохранность данных.'
  characteristics: 'Характеристики'
  blockTitle1: 'Базовые'
  blockTitle2: 'Универсальные'
  blockTitle3: 'Быстрые'
  blockSubtitle1: 'Для хранения больших объемов данных, которые не&nbsp;нужно часто читать или перезаписывать.'
  blockSubtitle2: 'Для размещения системных дисков виртуальных&nbsp;машин.'
  blockSubtitle3: 'Для рабочих нагрузок, требующих повышенной производительности ввода/вывода операций.'
  buttonText: 'Рассчитайте стоимость'
  month: 'месяц'
  gb: 'ГБ'
</i18n>

<template>
  <s-section
    :title="$t('title')"
    :subtitle="$t('subtitle')"
    type="gray"
    class="volumes"
    has-outside-content
  >
    <s-preloader v-if="!prices.length"></s-preloader>
    <div
      v-else
      class="container volumes__container"
    >
      <div class="row volumes__row">
        <div class="col-4">
          <div class="volumes__item">
            <h4
              class="volumes__item-title"
              v-html="$t('blockTitle1')"
            ></h4>

            <p
              class="volumes__text"
              v-html="$t('blockSubtitle1')"
            ></p>
            <p
              class="volumes__table-title"
              v-html="$t('characteristics')"
            ></p>
            <table class="volumes__table">
              <tbody>
                <tr
                  v-for="column in columns"
                  :key="column.dataIndex"
                >
                  <th> {{ column.title }}</th>
                  <td> {{ basicDiskItems[column.dataIndex] }}</td>
                </tr>
              </tbody>
            </table>

            <p class="volumes__price">
              1{{ $t('gb') }} × {{
                $priceFormatter(basicDiskPrice.value * PERIOD_FACTOR / KOPECKS_IN_RUBLE)
              }}/{{ $t('month') }}
            </p>
          </div>
        </div>

        <div class="col-4">
          <div class="volumes__item">
            <h4
              class="volumes__item-title"
              v-html="$t('blockTitle2')"
            >
            </h4>

            <p
              class="volumes__text"
              v-html="$t('blockSubtitle2')"
            ></p>
            <p
              class="volumes__table-title"
              v-html="$t('characteristics')"
            ></p>
            <table class="volumes__table">
              <tbody>
                <tr
                  v-for="column in columns"
                  :key="column.dataIndex"
                >
                  <th> {{ column.title }}</th>
                  <td> {{ universalDiskItems[column.dataIndex] }}</td>
                </tr>
              </tbody>
            </table>
            <p class="volumes__price">
              1{{ $t('gb') }} × {{
                $priceFormatter(universalDiskPrice.value * PERIOD_FACTOR / KOPECKS_IN_RUBLE)
              }}/{{ $t('month') }}
            </p>
          </div>
        </div>

        <div class="col-4">
          <div class="volumes__item">
            <h4
              class="volumes__item-title"
              v-html="$t('blockTitle3')"
            ></h4>

            <p
              class="volumes__text"
              v-html="$t('blockSubtitle3')"
            ></p>

            <p
              class="volumes__table-title"
              v-html="$t('characteristics')"
            ></p>
            <table class="volumes__table">
              <tbody>
                <tr
                  v-for="column in columns"
                  :key="column.dataIndex"
                >
                  <th> {{ column.title }}</th>
                  <td> {{ fastDiskItems[column.dataIndex] }}</td>
                </tr>
              </tbody>
            </table>
            <p class="volumes__price">
              1{{ $t('gb') }} × {{
                $priceFormatter(fastDiskPrice.value * PERIOD_FACTOR / KOPECKS_IN_RUBLE)
              }}/{{ $t('month') }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="container">
      <nuxt-link
        :to="localePath('/services/cloud/servers/prices/')"
        class="volumes__btn btn btn--lg btn--dark"
        v-html="$t('buttonText')"
      ></nuxt-link>
    </div>
  </s-section>
</template>

<script>
import {HOURS_IN_DAY, DAYS_IN_MONTH, KOPECKS_IN_RUBLE} from '~/helpers/constants'

import {Resource} from '~/helpers/prices/vpc/enum'
import {mapActions, mapGetters} from 'vuex'

const PERIOD_FACTOR = HOURS_IN_DAY * DAYS_IN_MONTH

const columns = {
  en: [
    {
      title: 'Type',
      dataIndex: 'type'
    },
    {
      title: 'Data Transfer Rate',
      dataIndex: 'throughput'
    },
    {
      title: 'Read',
      dataIndex: 'read'
    },
    {
      title: 'Write',
      dataIndex: 'write'
    }
  ],
  ru: [
    {
      title: 'Тип',
      dataIndex: 'type'
    },
    {
      title: 'Пропускная способность ',
      dataIndex: 'throughput'
    },
    {
      title: 'Чтение',
      dataIndex: 'read'
    },
    {
      title: 'Запись',
      dataIndex: 'write'
    }
  ]
}

export default {
  name: 'CloudServersVolumesConfigurations',
  async fetch() {
    if (this.getVpcCloudServersVolumesConfigurationsPrices.length) {
      this.prices = this.getVpcCloudServersVolumesConfigurationsPrices
    } else {
      await this.fetchVpcPrices()

      this.prices = this.getVpcCloudServersVolumesConfigurationsPrices
    }
  },
  fetchOnServer: true,
  data() {
    return {
      PERIOD_FACTOR,
      KOPECKS_IN_RUBLE,
      columns: columns[this.$i18n.locale],
      prices: []
    }
  },
  computed: {
    basicDiskPrice() {
      return this.prices.find(price => price.resource === Resource.VOLUME_GIGABYTES_BASIC)
    },
    universalDiskPrice() {
      return this.prices.find(price => price.resource === Resource.VOLUME_GIGABYTES_UNIVERSAL)
    },
    fastDiskPrice() {
      return this.prices.find(price => price.resource === Resource.VOLUME_GIGABYTES_FAST)
    },
    basicDiskItems() {
      return this.getCommonItems(this.basicDiskPrice)
    },
    universalDiskItems() {
      return this.getCommonItems(this.universalDiskPrice)
    },
    fastDiskItems() {
      return this.getCommonItems(this.fastDiskPrice)
    },
    ...mapGetters('rest-client/prices/vpc', ['getVpcCloudServersVolumesConfigurationsPrices'])
  },
  methods: {
    getCommonItems(price) {
      if (!price) {
        return
      } else {

        const {type, throughput, read, write} = price

        return {type: type[this.$i18n.locale], throughput: throughput[this.$i18n.locale], read, write}
      }
    },
    ...mapActions('rest-client/prices/vpc', ['fetchVpcPrices'])
  }
}
</script>

<style lang="scss">
.volumes__container {
  width: 100%;
  overflow-y: hidden;
}

.volumes__row {
  margin-bottom: 40px;

  @media (max-width: $breakpoint-xl) {
    width: 1440px;
    overflow-y: scroll;
    padding-top: 16px;
    padding-bottom: 16px;
  }

  @media (max-width: $breakpoint-md) {
    margin-bottom: 32px;
  }
}

.volumes__item {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  background-color: #FFF;
  border-radius: 8px;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08);
}

.volumes__item-title {
  margin: 0 0 28px;

  font-weight: 400;
  font-size: 24px;
  line-height: 32px;
  color: $primary;
}

.volumes__table-title {
  color: rgba($primary, 0.6);
}

.volumes__table {
  width: 100%;
  margin-bottom: 18px;
  text-align: left;

  tbody {
    display: grid;
    grid-auto-flow: row;
    grid-row-gap: 10px;
  }

  tr {
    display: grid;
    grid-template-columns: repeat(2, auto);
    grid-column-gap: 20px;
  }

  th {
    font-weight: 400;
  }

  td {
    text-align: right;
  }
}

.volumes__price {
  margin: auto 0 0;

  font-weight: 500;
  color: $secondary;
}

.volumes__btn {
  display: flex;
  width: fit-content;
  margin: 0 auto;

  @media (max-width: $breakpoint-sm) {
    width: 100%;
  }
}
</style>